import React, { useCallback, useEffect, useMemo, useState } from "react";
import SlidePanel from "./SlidePanel";
import { nearestShips } from "../helpers/nearby";
import { formatDistance, compassPoint, UNIT_LABEL, UNIT_NAME } from "../helpers/geo";

const PAGE = 10;
const MAX = 50;

const geoError = (e) => {
  if (e?.code === 1) return "Location permission was denied. Allow it for this site in your browser settings and try again.";
  if (e?.code === 3) return "Finding your location took too long. Try again.";
  return "Couldn't work out where you are. Try again, or use \"Go to coordinates\" in the map tools.";
};

// "Wrecks near me": the closest wrecks to the person's location, nearest first.
// Location is read in the browser and only used here to measure distances; it is never sent to the server.
export default function NearbyPanel({ ships, filtersActive, point, unit, onUnit, onLocated, onSelect, onSurprise, onClose }) {
  const [state, setState] = useState("idle"); // idle | locating | error
  const [error, setError] = useState("");
  const [count, setCount] = useState(PAGE);

  const locate = useCallback(() => {
    if (!navigator.geolocation) {
      setState("error");
      setError("Your browser doesn't support finding your location.");
      return;
    }
    setState("locating");
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setState("idle"); onLocated([pos.coords.latitude, pos.coords.longitude]); },
      (e) => { setState("error"); setError(geoError(e)); },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  }, [onLocated]);

  // first open without a known position: ask straight away
  useEffect(() => { if (!point) locate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => (point ? nearestShips(ships, point, count) : []), [ships, point, count]);
  const canMore = point && count < MAX && ships.length > count;

  return (
    <SlidePanel title="Wrecks near me" subtitle={point && results[0] ? `Closest: ${formatDistance(results[0].distanceM, unit)}` : "Closest wrecks to you"} onClose={onClose}>
      <div className="nearby-bar">
        <button type="button" className="panel-secondary nearby-locate" onClick={locate} disabled={state === "locating"}>
          {state === "locating" ? "Locating…" : point ? "↻ Update my location" : "📍 Find my location"}
        </button>
        <select className="nearby-unit" value={unit} onChange={(e) => onUnit(e.target.value)} aria-label="Distance unit">
          {Object.keys(UNIT_LABEL).map((u) => <option key={u} value={u}>{UNIT_NAME[u]}</option>)}
        </select>
      </div>

      {state === "error" && <p className="panel-error" role="alert">{error}</p>}
      {!point && state === "locating" && <p className="panel-muted">Asking your browser for your location…</p>}
      {filtersActive && <p className="panel-hint">Filters are on, so only wrecks that match them are listed.</p>}

      {point && ships.length === 0 && <p className="panel-muted">There are no wrecks to show{filtersActive ? " with these filters" : " yet"}.</p>}

      {results.length > 0 && (
        <ol className="nearby-list">
          {results.map(({ ship, distanceM, bearing }, i) => {
            const meta = [ship.type, ship.date].filter((v) => v && v !== "unknown");
            return (
              <li key={ship.id}>
                <button type="button" className="panel-item nearby-item" onClick={() => onSelect(ship)}>
                  <span className="nearby-rank" aria-hidden="true">{i + 1}</span>
                  <span className="panel-thumb">
                    {ship.images?.[0] ? <img src={ship.images[0]} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span>⚓</span>}
                  </span>
                  <span className="panel-item-main">
                    <h3>{ship.title}</h3>
                    {meta.length > 0 && <span className="panel-hint">{meta.join(" · ")}</span>}
                    <span className="nearby-dist"><strong>{formatDistance(distanceM, unit)}</strong> {compassPoint(bearing)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {canMore && <button type="button" className="panel-link" onClick={() => setCount((c) => Math.min(MAX, c + PAGE))}>Show {Math.min(PAGE, MAX - count)} more</button>}

      <button type="button" className="panel-secondary" onClick={onSurprise}>🎲 Surprise me with a random wreck</button>
    </SlidePanel>
  );
}
